/**
 * Poker-table frame. Felt hosts everything a real poker view shows:
 *
 *   (top)    5 opponent AIs in a shallow arc
 *            5 community cards on the felt
 *            Pot badge below the cards
 *   (bottom) Hero avatar at the bottom edge of the felt
 *
 * The hero's hole cards + stack + AI mascot live in `HeroPanel` BELOW
 * the table (outside the felt) — matches real poker cam framing where
 * you see your own hand clearly while the avatar sits "at the table".
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import feltPatternUrl from '@/assets/table/felt-pattern.svg?url';
import { BbSubtitle } from '@/components/bb/BbSubtitle';
import { CardFace } from '@/components/cards/CardFace';
import { BetChip } from '@/components/chips/BetChip';
import { DealerPuck, type DealerSeat } from '@/components/table/DealerPuck';
import type { Card } from '@/engine/types';

export type TableProps = {
  pot?: number;
  /** 5 community cards; missing slots render as "undealt" placeholders. */
  board?: ReadonlyArray<Card>;
  /** One-line showdown attribution shown during between-hands. */
  winnerLine?: string | null;
  /** BB size for unit conversion in the pot subtitle. */
  bbSize?: number;
  /** Hero's street contribution + last-action label — rendered as a thin
   *  band inside the felt above the pot only when active. The hero
   *  avatar itself lives in HeroPanel below the table. */
  heroStreetContribution?: number;
  heroStreetActionLabel?: string;
  /** Which seat currently holds the dealer button — drives the
   *  independently-animated `DealerPuck`. `null` = unknown / pre-hand. */
  dealerSeat?: DealerSeat | null;
  /** Opponent ring — pinned to the top of the felt. */
  children?: ReactNode;
  /** Absolute-positioned overlays (e.g., action log toggle). */
  overlay?: ReactNode;
};

export function Table({
  pot,
  board = [],
  winnerLine,
  bbSize = 0,
  heroStreetContribution,
  heroStreetActionLabel,
  dealerSeat = null,
  children,
  overlay,
}: TableProps) {
  const slots = Array.from({ length: 5 }, (_, i) => board[i]);
  // Pot-receive pulse when value grows.
  const prevPot = useRef(pot ?? 0);
  const [pulseKey, setPulseKey] = useState(0);
  useEffect(() => {
    const next = pot ?? 0;
    if (next > prevPot.current) setPulseKey((n) => n + 1);
    prevPot.current = next;
  }, [pot]);

  const hasHeroBet = typeof heroStreetContribution === 'number' && heroStreetContribution > 0;

  return (
    <section className="relative flex-1 min-h-0 overflow-hidden" data-testid="table">
      {/* Felt oval with tileable diamond-weave pixel pattern. */}
      <div
        className="absolute inset-3 rounded-[40%] bg-felt border-8 border-felt-rim shadow-[inset_0_0_80px_rgba(0,0,0,0.55),0_0_0_2px_hsl(var(--color-accent)/0.15)]"
        style={{
          backgroundImage: `url(${feltPatternUrl})`,
          backgroundRepeat: 'repeat',
          backgroundSize: '32px 32px',
          imageRendering: 'pixelated',
        }}
      />
      {/* Secondary inner glow — subtle bloodmoon wash. */}
      <div
        aria-hidden="true"
        className="absolute inset-3 rounded-[40%] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, hsl(var(--color-accent) / 0.08) 0%, transparent 65%)',
        }}
      />

      {/* Content stack — opponent ring pinned top with generous `pt-12`
          for thinking-bubble clearance above avatars; community + pot +
          hero street band packed tight at bottom via `mt-auto` spacer.
          This concentrates empty vertical space into the top gap (where
          thinking bubbles live) rather than splitting it across middle
          gaps, keeping opponent overlays inside the clipped Table. */}
      <div className="relative z-10 flex h-full flex-col px-2 pt-12 pb-2">
        {/* Opponent ring — top band. */}
        <div className="shrink-0">{children}</div>

        {/* Flex spacer pushes community/pot/hero toward bottom. */}
        <div className="flex-1 min-h-0" aria-hidden="true" />

        {/* Middle-bottom: community cards + winner line + pot. */}
        <div className="flex flex-col items-center gap-1 shrink-0 pointer-events-none">
          <div className="flex gap-1" data-testid="board">
            {slots.map((c, i) => (
              <CardFace key={c ?? `slot-${i}`} card={c} size="sm" backLabel="undealt" />
            ))}
          </div>
          {winnerLine ? (
            <div
              className="pointer-events-auto font-display text-xs uppercase tracking-widest text-warning bg-bg-deeper/90 border-2 border-warning/70 rounded-sm px-3 py-1 glow-gold"
              data-testid="winner-line"
            >
              {winnerLine}
            </div>
          ) : null}
          <div
            key={pulseKey}
            className="flex flex-col items-center gap-0.5 rounded-sm bg-bg-deeper/85 border-2 border-warning/70 px-3 py-1 shadow-[0_0_24px_hsl(var(--color-warning)/0.35)] pot-receive"
            data-testid="pot"
          >
            <div className="flex items-baseline gap-1.5">
              <span className="font-display text-[9px] uppercase tracking-widest text-warning">
                Pot
              </span>
              <span className="num text-xl text-warning text-glow-gold leading-none">
                {pot ?? 0}
              </span>
            </div>
            <BbSubtitle chips={pot ?? 0} bbSize={bbSize} tone="warning" className="text-[9px]" />
          </div>
        </div>

        {/* Hero street band — only rendered when hero has a street-level
            chip contribution or a live action label. The hero AVATAR lives
            in HeroPanel (outside the felt) so the table stays roomy. Chip
            animates up toward the pot above on street close. Tight `mt-1`
            keeps this band right under the pot so the top gap stays big. */}
        {heroStreetActionLabel || hasHeroBet ? (
          <div
            className="mt-1 flex flex-col items-center gap-0.5 shrink-0 pointer-events-none"
            data-testid="hero-seat"
          >
            {heroStreetActionLabel ? (
              <span
                className="font-display text-[9px] bg-accent-alt/15 text-accent-alt border-2 border-accent-alt/60 rounded-sm px-1.5 py-0.5 uppercase tracking-wider max-w-[120px] truncate"
                data-testid="hero-seat-last-action"
              >
                {heroStreetActionLabel}
              </span>
            ) : null}
            <BetChip amount={heroStreetContribution ?? 0} variant="hero" testId="hero-seat-bet" />
            {hasHeroBet ? (
              <BbSubtitle chips={heroStreetContribution ?? 0} bbSize={bbSize} tone="warning" />
            ) : null}
          </div>
        ) : (
          <div className="shrink-0 h-0" data-testid="hero-seat" />
        )}
      </div>

      {/* Floating dealer puck — positioned at SEAT_POS percentages inside
          the Table section. Independent of the flex content above. */}
      <DealerPuck seat={dealerSeat} />

      {overlay}
    </section>
  );
}
